import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Check, X, Trash2, Image as ImageIcon, Quote, CalendarDays, Video, Plus, Pencil } from "lucide-react";
import { AdminEventForm } from "./AdminEventForm";
import { EventFlyer } from "./EventFlyer";

const B = process.env.REACT_APP_BACKEND_URL;
const API = `${B}/api/admin/dealer-content`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } });
const BADGE = { pending: "bg-amber-100 text-amber-800", approved: "bg-green-100 text-green-700", rejected: "bg-slate-200 text-slate-600" };
const KINDS = [["photo", "Photos & Videos", ImageIcon], ["testimonial", "Testimonials", Quote], ["event", "Events", CalendarDays]];
const HINT = {
  photo: "Approved photos and videos show in the public Gallery and on the store's page.",
  testimonial: "Approved testimonials show on the public website.",
  event: "Approved events get an AI flyer, become a blog post and show on the store's page.",
};

const Body = ({ i, reload }) => {
  if (i.kind === "photo" && i.media_type === "video") return <><video src={`${B}${i.video_url}`} controls preload="metadata" className="w-full h-40 object-cover rounded-lg mb-2 bg-black" />{i.caption && <p className="text-sm text-slate-600">{i.caption}</p>}</>;
  if (i.kind === "photo") return <><img src={`${B}${i.image_url}`} alt={i.caption || "store"} className="w-full h-40 object-cover rounded-lg mb-2" />{i.caption && <p className="text-sm text-slate-600">{i.caption}</p>}</>;
  if (i.kind === "testimonial") return <><p className="text-sm text-slate-700 italic">"{i.text}"</p><p className="text-xs text-slate-500 mt-2">— {i.author_name}{i.author_title ? `, ${i.author_title}` : ""} · {i.rating}★</p></>;
  return <><p className="font-semibold text-navy text-sm">{i.title}</p><p className="text-xs text-slate-500">{i.start.replace("T", " ")}{i.end ? ` – ${i.end.replace("T", " ")}` : ""}</p>{i.description && <p className="text-sm text-slate-600 mt-1">{i.description}</p>}<div className="mt-2"><EventFlyer item={i} mode="admin" onChange={reload} /></div></>;
};

const Item = ({ i, canManage, act, reload }) => {
  const [editing, setEditing] = useState(false);
  const Icon = i.media_type === "video" ? Video : KINDS.find((k) => k[0] === i.kind)[2];
  if (editing) return <div className="md:col-span-2 lg:col-span-3"><AdminEventForm initial={i} onDone={() => { setEditing(false); reload(); }} />
    <Button size="sm" variant="ghost" className="mt-1" onClick={() => setEditing(false)} data-testid={`dealer-content-edit-cancel-${i.id}`}>Cancel</Button></div>;
  return (
    <Card className="border-slate-200" data-testid={`dealer-content-${i.id}`}>
      <CardContent className="p-4 space-y-2">
        <div className="flex items-center justify-between text-xs">
          <span className="flex items-center gap-1 font-semibold text-navy capitalize"><Icon className="w-4 h-4 text-teal" />{i.media_type === "video" ? "video" : i.kind}</span>
          <span className={`px-2 py-0.5 rounded-full capitalize ${BADGE[i.status]}`}>{i.status}</span>
        </div>
        <Body i={i} reload={reload} />
        <p className="text-xs text-slate-400">{i.location_name || "No store"} · by {i.submitted_by} · {new Date(i.created_at).toLocaleDateString()}</p>
        {canManage && <div className="flex gap-2 pt-2 border-t border-slate-100">
          {i.status !== "approved" && <Button size="sm" className="flex-1 bg-teal hover:bg-teal-dark text-white" onClick={() => act(i.id, "approve")} data-testid={`dealer-content-approve-${i.id}`}><Check className="w-4 h-4 mr-1" />Approve</Button>}
          {i.status !== "rejected" && <Button size="sm" variant="outline" className="flex-1" onClick={() => act(i.id, "reject")} data-testid={`dealer-content-reject-${i.id}`}><X className="w-4 h-4 mr-1" />Reject</Button>}
          {i.kind === "event" && <Button size="sm" variant="outline" onClick={() => setEditing(true)} data-testid={`dealer-content-edit-${i.id}`}><Pencil className="w-4 h-4" /></Button>}
          <Button size="sm" variant="outline" className="text-red-600" onClick={() => act(i.id, "delete")} data-testid={`dealer-content-delete-${i.id}`}><Trash2 className="w-4 h-4" /></Button>
        </div>}
      </CardContent>
    </Card>
  );
};

// Admin review queue for dealer photos/videos, testimonials and store events, one tab per type
export const DealerContentReview = ({ canManage }) => {
  const [kind, setKind] = useState("photo");
  const [status, setStatus] = useState("pending");
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({});
  const [adding, setAdding] = useState(false);
  const load = useCallback(() => {
    let live = true;
    axios.get(API, { ...auth(), params: { kind, ...(status === "all" ? {} : { status }) } }).then((r) => live && setItems(r.data)).catch(() => toast.error("Couldn't load submissions"));
    axios.get(`${API}/counts`, auth()).then((r) => live && setCounts(r.data)).catch(() => {});
    return () => { live = false; };
  }, [kind, status]);
  useEffect(() => load(), [load]);
  const act = async (id, action) => {
    if (action === "delete" && !window.confirm("Delete this submission? Approved items are also removed from the website.")) return;
    try { if (action === "delete") await axios.delete(`${API}/${id}`, auth()); else await axios.post(`${API}/${id}/${action}`, {}, auth()); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Action failed"); }
  };
  return (
    <div className="space-y-4" data-testid="dealer-content-review">
      <Tabs value={kind} onValueChange={(k) => { setKind(k); setAdding(false); }}>
        <TabsList>{KINDS.map(([k, label]) => (
          <TabsTrigger key={k} value={k} data-testid={`dealer-content-tab-${k}`}>{label}{counts[k] ? <span className="ml-1.5 px-1.5 rounded-full bg-amber-500 text-white text-[10px]">{counts[k]}</span> : null}</TabsTrigger>
        ))}</TabsList>
      </Tabs>
      <p className="text-xs text-slate-500">{HINT[kind]}</p>
      <div className="flex gap-2 flex-wrap items-center">
        {["pending", "approved", "rejected", "all"].map((s) => (
          <Button key={s} size="sm" variant={status === s ? "default" : "outline"} onClick={() => setStatus(s)} className="capitalize" data-testid={`dealer-content-filter-${s}`}>{s}</Button>
        ))}
        {kind === "event" && canManage && <Button size="sm" className="ml-auto bg-teal hover:bg-teal-dark text-white" onClick={() => setAdding(!adding)} data-testid="dealer-content-add-event"><Plus className="w-4 h-4 mr-1" />Add event</Button>}
      </div>
      {adding && <AdminEventForm onDone={() => { setAdding(false); setStatus("approved"); load(); }} />}
      {items.length === 0 && <p className="text-sm text-slate-500" data-testid="dealer-content-empty">Nothing here.</p>}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((i) => <Item key={i.id} i={i} canManage={canManage} act={act} reload={load} />)}
      </div>
    </div>
  );
};
